import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import api from '../services/api';
import { formatCurrency } from '../lib/format';

const API_PATH_PREFIX = {
  quote: 'quotes',
  estimate: 'estimates',
  invoice: 'invoices',
  'credit-note': 'credit-notes',
};

const DISPLAY_LABELS = {
  quote: 'Quote',
  estimate: 'Estimate',
  invoice: 'Invoice',
  'credit-note': 'Credit Note',
};

export const SendDocumentModal = ({ open, onClose, documentType, documentId, documentNumber, onSent }) => {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [secureLink, setSecureLink] = useState(null);
  const [overrideTo, setOverrideTo] = useState('');
  const [overrideCc, setOverrideCc] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [copiedLink, setCopiedLink] = useState(false);

  const label = DISPLAY_LABELS[documentType] || documentType;
  const apiPrefix = API_PATH_PREFIX[documentType] || `${documentType}s`;

  useEffect(() => {
    if (open && !sent) {
      loadPreview();
    }
    if (!open) {
      setSent(false);
      setSecureLink(null);
      setOverrideTo('');
      setOverrideCc('');
      setCustomMessage('');
      setPreview(null);
      setCopiedLink(false);
    }
  }, [open, documentId, sent]);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const endpoint = `/${apiPrefix}/${documentId}/delivery/prepare`;
      const response = await api.post(endpoint, {
        override_to: overrideTo || null,
        override_cc: overrideCc || null,
        custom_message: customMessage || null,
      });
      setPreview(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load preview');
      console.error('Preview error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const endpoint = `/${apiPrefix}/${documentId}/delivery/send`;
      const response = await api.post(endpoint, {
        override_to: overrideTo || null,
        override_cc: overrideCc || null,
        custom_message: customMessage || null,
      });

      const token = response.data.secure_token;
      const link = `${window.location.origin}/view/${token}`;
      setSecureLink(link);
      setSent(true);
      toast.success(`${label} sent successfully`);

      if (onSent) {
        onSent();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send document');
      console.error('Send error:', err);
    } finally {
      setSending(false);
    }
  };

  const copyLink = () => {
    if (secureLink) {
      navigator.clipboard.writeText(secureLink);
      setCopiedLink(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopiedLink(false), 2000);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="send-document-modal">
        <DialogHeader>
          <DialogTitle>
            {sent ? 'Document Sent' : `Send ${label}`}
          </DialogTitle>
        </DialogHeader>

        {sent ? (
          <div className="space-y-4" data-testid="send-success-state">
            <p className="text-emerald-800 font-medium bg-emerald-50 border border-emerald-200 rounded-md p-3">
              Your {label.toLowerCase()} has been sent successfully!
            </p>

            <div className="space-y-2">
              <Label>Secure Public Link</Label>
              <div className="flex gap-2">
                <Input
                  value={secureLink}
                  readOnly
                  className="font-mono text-sm"
                  data-testid="secure-link-input"
                />
                <Button variant="outline" onClick={copyLink} data-testid="copy-link-button">
                  Copy
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                Share this link with your client or send it via your preferred method.
              </p>
            </div>

            {preview && (
              <div className="bg-slate-50 p-4 rounded-md border border-slate-200 space-y-2">
                <p className="text-xs text-slate-500 font-medium">Sent to:</p>
                <p className="text-sm text-slate-900">{preview.recipient_to}</p>
                {preview.recipient_cc && (
                  <>
                    <p className="text-xs text-slate-500 font-medium mt-2">CC:</p>
                    <p className="text-sm text-slate-900">{preview.recipient_cc}</p>
                  </>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {loading ? (
              <p className="text-sm text-slate-500">Loading preview…</p>
            ) : preview ? (
              <>
                <div className="bg-slate-50 p-4 rounded-md border border-slate-200 space-y-3">
                  <div>
                    <p className="text-xs text-slate-500 font-medium">Document</p>
                    <p className="text-sm text-slate-900 font-medium">
                      {label} #{documentNumber}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 font-medium">Subject</p>
                    <p className="text-sm text-slate-900">{preview.subject}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="custom-message-input">Personal message (optional)</Label>
                    <Textarea
                      id="custom-message-input"
                      value={customMessage}
                      onChange={(e) => setCustomMessage(e.target.value)}
                      placeholder="Add a short note to include in the email body…"
                      rows={4}
                      data-testid="custom-message-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Recipient (To)</Label>
                    <Input
                      type="email"
                      value={overrideTo || preview.recipient_to}
                      onChange={(e) => setOverrideTo(e.target.value)}
                      placeholder="client@example.com"
                      data-testid="recipient-to-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>CC (optional)</Label>
                    <Input
                      type="email"
                      value={overrideCc || preview.recipient_cc || ''}
                      onChange={(e) => setOverrideCc(e.target.value)}
                      placeholder="manager@example.com"
                      data-testid="recipient-cc-input"
                    />
                  </div>
                </div>

                <p className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-md p-3">
                  A secure link and PDF attachment will be emailed to the recipient.
                </p>
              </>
            ) : (
              <p className="text-sm text-slate-500">Failed to load preview</p>
            )}
          </div>
        )}

        <DialogFooter>
          {sent ? (
            <Button onClick={onClose} data-testid="close-modal-button">Close</Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                onClick={handleSend}
                disabled={sending || loading || !preview}
                data-testid="send-button"
              >
                {sending ? 'Sending...' : `Send ${label}`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
